import React, { useEffect, useState } from "react";
import { Megaphone, Layout, Newspaper, Trophy, Users2, ShieldCheck } from "lucide-react";
import client, { API } from "../lib/api";
import { SectionTitle, Card, Pill, Loading } from "../components/common";
const SECTIONS = {
  SEC_MASTHEAD: { icon: Layout, title: "Masthead", desk: "Rudo Nyathi", blurb: "Global header leaderboard on every page, home hero companion and the reserved-adjacent platform strip." },
  SEC_EDITORIAL: { icon: Newspaper, title: "Editorial", desk: "Tawanda Sibanda", blurb: "In-feed units on News / Articles / Updates, in-article mid-body and story sidebar." },
  SEC_FANTASY: { icon: Trophy, title: "Fantasy", desk: "Nyasha Gumbo", blurb: "Deadline takeover, squad-picker banner and the fantasy chat sidebar." },
  SEC_COMMUNITY: { icon: Users2, title: "Community", desk: "Blessing Mhlanga", blurb: "Timeline native units, forum board headers & thread sidebars, club sidebar and footer board." },
  SEC_RESERVED: { icon: ShieldCheck, title: "Reserved · League partner", desk: "PSL League Office", blurb: "Castle Lager Premier Soccer League acknowledgement. NOT sellable by platform sales." },
};

const STATUS_TONE = { available: "gold", sold: "green", flying: "green", reserved: "muted" };

export default function AdvertisePage() {
  const [slots, setSlots] = useState(null);
  useEffect(() => { client.get("/ads").then((r) => setSlots(r.data)).catch(() => setSlots([])); }, []);

  const grouped = {};
  (slots || []).forEach((s) => { (grouped[s.section] = grouped[s.section] || []).push(s); });
  const order = ["SEC_MASTHEAD", "SEC_EDITORIAL", "SEC_FANTASY", "SEC_COMMUNITY", "SEC_RESERVED"];
  const totalSellable = (slots || []).filter((s) => !s.reserved).length;
  const available = (slots || []).filter((s) => s.status === "available").length;

  return (
    <div data-testid="advertise-page">
      <div className="bg-green text-cream rounded-sm p-8 grain relative overflow-hidden mb-8">
        <Megaphone className="absolute -right-6 -bottom-6 text-gold/20" size={160} />
        <Pill tone="gold">Rate card 2026</Pill>
        <h1 className="font-head font-black text-3xl md:text-4xl mt-3 tracking-tight">Advertise on premierleaguezw.com</h1>
        <p className="text-cream/80 mt-2 max-w-2xl">Reach millions of Zimbabwean football fans across four dedicated inventory sections. Platform advertising is booked directly with our internal sales desks — league title-sponsor inventory is never resold.</p>
        {slots && (
          <div className="flex flex-wrap gap-6 mt-5">
            <Stat n={totalSellable} label="Sellable slots" />
            <Stat n={available} label="Available now" />
            <Stat n={4} label="Sales sections" />
          </div>
        )}
      </div>

      <div className="flex justify-end mb-4">
        <a href={`${API}/ads/register.csv`} className="inline-flex items-center gap-2 px-4 py-2 bg-green text-cream font-bold rounded-sm hover:bg-ink transition-colors text-sm" data-testid="download-register">
          Download asset register (CSV)
        </a>
      </div>
      <SectionTitle kicker="Inventory">Sales sections</SectionTitle>
      <div className="grid sm:grid-cols-2 gap-4 mb-10">
        {["SEC_MASTHEAD", "SEC_EDITORIAL", "SEC_FANTASY", "SEC_COMMUNITY"].map((code) => {
          const s = SECTIONS[code];
          return (
            <Card key={code} className="p-6" data-testid={`advertise-${code}`}>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-11 h-11 rounded-sm bg-gold text-ink flex items-center justify-center"><s.icon /></div>
                <div><div className="font-head font-bold text-green text-lg">{s.title}</div><div className="text-xs text-ink/50">{code} · {s.desk}</div></div>
              </div>
              <p className="text-sm text-ink/70">{s.blurb}</p>
            </Card>
          );
        })}
      </div>

      <SectionTitle kicker="All opportunities" testid="advertise-inventory">Full slot inventory</SectionTitle>
      {!slots ? <Loading /> : (
        <div className="space-y-6">
          {order.filter((c) => grouped[c]).map((code) => {
            const meta = SECTIONS[code];
            return (
              <Card key={code} className="overflow-hidden" data-testid={`inventory-${code}`}>
                <div className="bg-green text-cream px-4 py-3 flex items-center gap-2">
                  <meta.icon size={18} className="text-gold" />
                  <span className="font-head font-bold">{meta.title}</span>
                  <span className="text-cream/60 text-xs">{code} · {meta.desk}</span>
                  <span className="ml-auto text-cream/60 text-xs">{grouped[code].length} slot{grouped[code].length === 1 ? "" : "s"}</span>
                </div>
                <div className="overflow-x-auto scrollbar-thin">
                  <table className="w-full text-sm min-w-[620px]">
                    <thead>
                      <tr className="text-left text-xs uppercase tracking-wider text-ink/50 border-b border-pslborder">
                        <th className="px-4 py-2">Slot</th><th className="px-2 py-2">Pages</th><th className="px-2 py-2">Size</th><th className="px-2 py-2">Status</th><th className="px-4 py-2">Advertiser</th>
                      </tr>
                    </thead>
                    <tbody>
                      {grouped[code].map((s) => (
                        <tr key={s.slot_id} className="border-b border-pslborder last:border-0 hover:bg-cream" data-testid={`slot-row-${s.slot_id}`}>
                          <td className="px-4 py-2.5 font-mono text-xs font-bold text-green">{s.slot_id}</td>
                          <td className="px-2 py-2.5 text-ink/70">{s.page_pattern}</td>
                          <td className="px-2 py-2.5 text-ink/70">{s.size}</td>
                          <td className="px-2 py-2.5"><Pill tone={STATUS_TONE[s.status] || "muted"}>{s.reserved ? "reserved" : s.status}</Pill></td>
                          <td className="px-4 py-2.5">{s.advertiser || <span className="text-ink/30">—</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Card className="p-6 mt-6 bg-cream border-dashed border-gold">
        <p className="text-sm text-ink/70">No self-serve checkout in v1. To book, contact the relevant desk at <span className="font-bold text-green">sales@premierleaguezw.com</span>. Category conflicts with live league sponsors are routed to Super Admin — Digital. The reserved league-partner strip is not available for platform sale.</p>
      </Card>
    </div>
  );
}

function Stat({ n, label }) {
  return <div><div className="font-head font-black text-3xl text-gold tabular">{n}</div><div className="text-xs text-cream/60 uppercase tracking-wider">{label}</div></div>;
}
